'use client';

import {
  animate,
  useMotionValue,
  useDragControls,
  PanInfo,
  AnimationPlaybackControls,
} from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import useMeasure from 'react-use-measure';

export const useSlider = () => {
  const FAST_DURATION = 25;
  const SLOW_DURATION = 75;

  const [isHovered, setIsHovered] = useState(false);
  const [ref, { width }] = useMeasure();
  const xTranslation = useMotionValue(0);
  const dragControls = useDragControls();
  const [isDragging, setIsDragging] = useState(false);

  // Store animation controls to dynamically update them
  const animationControls = useRef<AnimationPlaybackControls | null>(null);

  useEffect(() => {
    if (isDragging || width === 0) return; // ✅ Stop animation if dragging or width is not ready

    const finalPosition = -width / 2 - 8;

    // Stop the previous animation before starting a new one
    if (animationControls.current) {
      animationControls.current.stop();
    }

    // Create a new animation and store the controls
    animationControls.current = animate(
      xTranslation,
      [xTranslation.get(), finalPosition],
      {
        ease: 'linear',
        duration: isHovered ? SLOW_DURATION : FAST_DURATION, // ✅ Smoothly update speed
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: 0,
        onUpdate: (latest) => {
          if (latest <= finalPosition + 10) {
            xTranslation.set(0); // ✅ Instantly reset position
          }
        },
      }
    );

    return () => {
      if (animationControls.current) {
        animationControls.current.stop();
      }
    };
  }, [xTranslation, width, isHovered, isDragging]); // ✅ Depend on `isHovered` and `isDragging`

  const handleHoverStart = () => {
    setIsHovered(true);
    if (animationControls.current) {
      animationControls.current.stop();
      animationControls.current = animate(
        xTranslation,
        [xTranslation.get(), -width / 2 - 8],
        {
          ease: 'linear',
          duration: SLOW_DURATION, // ✅ Smooth transition to slower speed
          repeat: Infinity,
          repeatType: 'loop',
          repeatDelay: 0,
        }
      );
    }
  };

  const handleHoverEnd = () => {
    setIsHovered(false);
    if (animationControls.current) {
      animationControls.current.stop();
      animationControls.current = animate(
        xTranslation,
        [xTranslation.get(), -width / 2 - 8],
        {
          ease: 'linear',
          duration: FAST_DURATION, // ✅ Smooth transition to normal speed
          repeat: Infinity,
          repeatType: 'loop',
          repeatDelay: 0,
        }
      );
    }
  };

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = (_?: unknown, info?: PanInfo) => {
    if (!info) return;
    setIsDragging(false);

    const offset = info.offset.x;
    let velocity = info.velocity.x;

    if (Math.abs(velocity) > 1500) {
      velocity = velocity > 0 ? 1500 : -1500; // 🔥 Limit max velocity
    }

    animate(xTranslation, xTranslation.get() + offset + velocity * 0.5, {
      ease: 'linear',
      duration: 0.3,
    });
  };

  return {
    ref,
    width,
    xTranslation,
    dragControls,
    handleHoverStart,
    handleHoverEnd,
    handleDragStart,
    handleDragEnd,
  };
};
